import type { ReactNode } from 'react';

/**
 * Cells for the admin console's tables: a person/workspace identity cell with
 * an initials avatar, a status pill with a coloured dot, relative times with
 * the full date on hover, and a verified check. Presentation only.
 */

const AVATAR_COLOURS = [
  'bg-emerald-100 text-emerald-800',
  'bg-sky-100 text-sky-800',
  'bg-violet-100 text-violet-800',
  'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-800',
  'bg-teal-100 text-teal-800',
  'bg-indigo-100 text-indigo-800',
  'bg-lime-100 text-lime-800',
];

/** The same colour for the same key, every time. */
export function avatarColour(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLOURS[h % AVATAR_COLOURS.length]!;
}

export function initials(name: string | null | undefined, fallback: string): string {
  const source = (name ?? '').trim() || fallback;
  const parts = source.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0]![0]! + parts[1]![0]! : (parts[0] ?? '?').slice(0, 2);
  return letters.toUpperCase();
}

export function Avatar({ id, name, fallback, square = false }: { id: string; name: string | null; fallback: string; square?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-9 w-9 shrink-0 items-center justify-center text-xs font-semibold ${square ? 'rounded-lg' : 'rounded-full'} ${avatarColour(id)}`}
    >
      {initials(name, fallback)}
    </span>
  );
}

/** Avatar, a bold first line and a muted second line, in one column. */
export function IdentityCell({
  id,
  primary,
  secondary,
  square,
  name,
}: {
  id: string;
  primary: ReactNode;
  secondary?: ReactNode;
  square?: boolean;
  /** The text the initials come from, when `primary` is a link. */
  name?: string | null;
}) {
  const label = name || (typeof primary === 'string' ? primary : typeof secondary === 'string' ? secondary : id);
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar id={id} name={label} fallback={label} square={square} />
      <div className="min-w-0">
        <div className="truncate font-semibold text-slate-900">{primary}</div>
        {secondary && <div className="truncate text-xs text-slate-500">{secondary}</div>}
      </div>
    </div>
  );
}

export type PillTone = 'green' | 'red' | 'blue' | 'amber' | 'slate';

const PILL: Record<PillTone, { pill: string; dot: string }> = {
  green: { pill: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', dot: 'bg-emerald-500' },
  red: { pill: 'bg-red-50 text-red-700 ring-red-600/20', dot: 'bg-red-500' },
  blue: { pill: 'bg-sky-50 text-sky-700 ring-sky-600/20', dot: 'bg-sky-500' },
  amber: { pill: 'bg-amber-50 text-amber-800 ring-amber-600/20', dot: 'bg-amber-500' },
  slate: { pill: 'bg-slate-100 text-slate-700 ring-slate-500/15', dot: 'bg-slate-400' },
};

export function StatusPill({ tone, label, pulse = false }: { tone: PillTone; label: string; pulse?: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${PILL[tone].pill}`}>
      <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
        {pulse && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${PILL[tone].dot}`} />}
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${PILL[tone].dot}`} />
      </span>
      {label}
    </span>
  );
}

export function Chip({ tone, label }: { tone: PillTone; label: string }) {
  return (
    <span className={`inline-flex whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${PILL[tone].pill}`}>
      {label}
    </span>
  );
}

/** "3h ago" / "in 2d" / "Never", with the full date in a tooltip. */
export function relativeTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Math.round((now - t) / 1000);
  const abs = Math.abs(diff);
  const units: Array<[number, string]> = [
    [60, 's'],
    [60, 'm'],
    [24, 'h'],
    [30, 'd'],
    [12, 'mo'],
    [Number.POSITIVE_INFINITY, 'y'],
  ];
  if (abs < 45) return 'Just now';
  let value = abs;
  let unit = 's';
  for (const [size, name] of units) {
    unit = name;
    if (value < size) break;
    value = Math.floor(value / size);
  }
  return diff >= 0 ? `${value}${unit} ago` : `in ${value}${unit}`;
}

export function RelativeTime({ iso, never = 'Never' }: { iso: string | null | undefined; never?: string }) {
  if (!iso) return <span className="text-slate-400">{never}</span>;
  const d = new Date(iso);
  return (
    <time dateTime={iso} title={Number.isNaN(d.getTime()) ? undefined : d.toLocaleString()} className="whitespace-nowrap text-slate-600">
      {relativeTime(iso)}
    </time>
  );
}

export function VerifiedIcon({ verified }: { verified: boolean }) {
  return verified ? (
    <span title="Email verified" aria-label="Email verified" className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden="true">
        <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  ) : (
    <span title="Email not verified" aria-label="Email not verified" className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-50 text-amber-600">
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden="true">
        <path d="M12 8v5m0 3h.01" strokeLinecap="round" />
      </svg>
    </span>
  );
}

/** Segmented filter tabs with counts. */
export function FilterTabs<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<{ value: T; label: string; count?: number | null }>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex max-w-full overflow-x-auto rounded-lg bg-slate-100 p-1">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o.value)}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              on ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {o.label}
            {typeof o.count === 'number' && (
              <span className={`rounded-full px-1.5 text-xs tabular-nums ${on ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
